import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import { RiRefreshFill } from "react-icons/ri";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getRevenueByDisease } from "../../actions/revenueAction";
import { toast } from "react-toastify";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import DataTable, { createTheme } from "react-data-table-component";
import resetBtn from "../../assets/images/reset-arrow.svg";
import { IoIosArrowUp } from "react-icons/io";

createTheme(
  "solarized",
  {
    text: { primary: "#000", secondary: "#000" },
    background: { default: "#fff" },
    context: { background: "#cb4b16", text: "#FFFFFF" },
    divider: { default: "#DFE0EB" },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  },
  "dark"
);

const RECORDS_PER_PAGE = 100;

const RevenueByDisease = () => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalConsultations, setTotalConsultations] = useState(0);
  const [loader, setLoader] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateError, setDateError] = useState("");
  const [skip, setSkip] = useState(0);
  const [platform, setPlatform] = useState("");
  const [searchName, setSearchName] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (startDate && endDate && startDate > endDate) {
      setDateError("Start Date cannot be greater than End Date");
      return;
    }
    setDateError("");
    setLoader(true);
    try {
      const params = {
        limit: RECORDS_PER_PAGE,
        skip,
        sortBy: "revenue",
        sortDir: "desc",
      };
      if (startDate) params.startDate = moment(startDate).toISOString();
      if (endDate) params.endDate = moment(endDate).toISOString();
      if (platform) params.platform = platform;

      const res = await getRevenueByDisease(params);
      if (res?.data) {
        setTotalRevenue(res.data.totalRevenue ?? 0);
        setTotalConsultations(res.data.totalConsultations ?? 0);
        setTotalRows(res.data.totalRows ?? 0);
        setRows(res.data.rows ?? []);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load revenue data");
      setRows([]);
    } finally {
      setLoader(false);
    }
  }, [startDate, endDate, skip, platform]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setDateError("");
    setSkip(0);
    setPlatform("");
    setSearchName("");
  };

  const refreshTable = () => {
    setSkip(0);
    fetchData();
  };

  const filteredRows = searchName.trim()
    ? rows.filter(
        (r) =>
          r.diseaseName &&
          r.diseaseName.toLowerCase().includes(searchName.trim().toLowerCase())
      )
    : rows;
  const toggleCollapse = () => setIsOpen(!isOpen);
  const toggleFilterAccordion = () => setIsFilterOpen(!isFilterOpen);

  const columns = [
    { name: "Disease", selector: (row) => row.diseaseName || "—", minWidth: "280px" },
    {
      name: "Revenue $",
      selector: (row) =>
        Number(row.revenue).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
        }),
      minWidth: "120px",
    },
    {
      name: "Revenue %",
      selector: (row) => `${Number(row.revenuePercent).toFixed(2)}%`,
      minWidth: "100px",
    },
    { name: "Paid Consultations", selector: (row) => row.consultationCount, minWidth: "120px" },
  ];

  return (
    <>
      <div className="pt-2 pt-md-3">
        <Container fluid>
          <Row>
            <Col md="12">
              {/* Mobile filter collapse */}
              <div
                className={`custom-collapse ${isOpen ? "open" : ""} desktop_none mobile_block`}
              >
                <div className="custom-collapse-header" onClick={toggleCollapse}>
                  <span>
                    Filter
                    <i className="fa fa-filter" aria-hidden="true"></i>
                  </span>
                  <span className={`arrow ${isOpen ? "open" : ""}`}></span>
                </div>
                <div
                  className="custom-collapse-body"
                  style={{ maxHeight: isOpen ? "1000px" : "0" }}
                >
                  <div className={`collapse-content ${isOpen ? "open" : ""}`}>
                    <Card className="filter-card card">
                      <Card.Header>
                        <Card.Title className="text-white" as="h4">
                          Filter
                        </Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col xs={12} className="mb-2">
                            <div className="datepikker-btn d-flex flex-wrap datapiker-custom-alignment">
                              <Form.Group className="mr-2">
                                <Form.Label className="d-block mb-2 text-white">
                                  Search by name
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="Search disease"
                                  value={searchName}
                                  onChange={(e) => setSearchName(e.target.value)}
                                />
                              </Form.Group>
                              <Form.Group className="mr-2">
                                <Form.Label className="d-block mb-2 text-white">
                                  Start Date:
                                </Form.Label>
                                <div className="date-picker-container form-controll">
                                  <DatePicker
                                    showIcon
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    maxDate={new Date()}
                                    icon="fa fa-calendar"
                                    placeholderText="mm/dd/yyyy"
                                  />
                                </div>
                              </Form.Group>
                              <Form.Group className="mr-2">
                                <Form.Label className="d-block mb-2 text-white">
                                  End Date:
                                </Form.Label>
                                <div className="date-picker-container form-controll">
                                  <DatePicker
                                    showIcon
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    maxDate={new Date()}
                                    icon="fa fa-calendar"
                                    placeholderText="mm/dd/yyyy"
                                  />
                                </div>
                                <span style={{ color: "#ff777f" }}>{dateError}</span>
                              </Form.Group>
                              <Form.Group className="mr-2">
                                <Form.Label className="d-block mb-2 text-white">
                                  Select platform
                                </Form.Label>
                                <Form.Control
                                  as="select"
                                  value={platform}
                                  onChange={(e) => {
                                    setPlatform(e.target.value);
                                    setSkip(0);
                                  }}
                                >
                                  <option value="">Select</option>
                                  <option value="app">App</option>
                                  <option value="web">Website</option>
                                </Form.Control>
                              </Form.Group>
                              <Form.Group className="mr-2 align-self-sm-end">
                                <label className="d-none d-sm-block mb-2 form-label">&nbsp;</label>
                                <div className="d-flex filter-btns-holder">
                                  <button
                                    type="button"
                                    className="outline-button w-auto filter-reset-btn"
                                    onClick={resetFilter}
                                  >
                                    <img src={resetBtn} className="img-fluid mr-3" alt="reset" />
                                    Reset
                                  </button>
                                </div>
                              </Form.Group>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </div>

              {/* Desktop filter card */}
              <Card className="filter-card card mobile_none">
                <Card.Header style={{ cursor: "pointer" }} onClick={toggleFilterAccordion}>
                  <div className="d-flex align-items-center justify-content-between table-head">
                    <Card.Title className="text-white" as="h4">
                      Filter
                    </Card.Title>
                    <div
                      style={{
                        transform: isFilterOpen ? "rotate(0deg)" : "rotate(180deg)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <IoIosArrowUp />
                    </div>
                  </div>
                </Card.Header>
                {isFilterOpen && (
                  <Card.Body>
                    <Row>
                      <Col xl={12} sm={12}>
                        <div className="datepikker-btn  datapiker-custom-alignment ">
                          <div className="row">
                            <div className="col-lg-6">
                          <Form.Group className="mr-2">
                            <Form.Label className="d-block mb-2 text-white">
                              Search by name
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Search disease"
                              value={searchName}
                              onChange={(e) => setSearchName(e.target.value)}
                            />
                          </Form.Group>
                          </div>
                          <div className="col-lg-6">
                             <Form.Group className="mr-2">
                            <Form.Label className="d-block mb-2 text-white">
                              Select platform
                            </Form.Label>
                            <Form.Control
                              as="select"
                              value={platform}
                              onChange={(e) => {
                                setPlatform(e.target.value);
                                setSkip(0);
                              }}
                            >
                              <option value="">Select</option>
                              <option value="app">App</option>
                              <option value="web">Website</option>
                            </Form.Control>
                          </Form.Group>

                          </div>
                          <div className="col-lg-12 d-flex flex-wrap">
                          <Form.Group className="mr-2 reveune-col-full">
                            <Form.Label className="d-block mb-2 text-white">
                              Start Date:
                            </Form.Label>
                            <div className="date-picker-container form-controll">
                              <DatePicker
                                showIcon
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                maxDate={new Date()}
                                icon="fa fa-calendar"
                                placeholderText="mm/dd/yyyy"
                              />
                            </div>
                          </Form.Group>
                          <Form.Group className="mr-2 reveune-col-full">
                            <Form.Label className="d-block mb-2 text-white">
                              End Date:
                            </Form.Label>
                            <div className="date-picker-container form-controll">
                              <DatePicker
                                showIcon
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                maxDate={new Date()}
                                icon="fa fa-calendar"
                                placeholderText="mm/dd/yyyy"
                              />
                            </div>
                            <span style={{ color: "#ff777f" }}>{dateError}</span>
                          </Form.Group>
                         
                          <Form.Group className="mr-2 align-self-sm-end ml-auto reveune-col-full">
                            <label className="d-none d-sm-block mb-2 form-label ">&nbsp;</label>
                            <div className="d-flex filter-btns-holder">
                              <button
                                type="button"
                                className="outline-button w-auto filter-reset-btn"
                                onClick={resetFilter}
                              >
                                <img src={resetBtn} className="img-fluid mr-3" alt="reset" />
                                Reset
                              </button>
                            </div>
                          </Form.Group>
                          </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                )}
              </Card>

              {/* Summary tiles - under filters */}
              <Row className="mb-3">
                <Col md={6} lg={4}>
                  <Card className="card-stats revenue-disease">
                    <Card.Body>
                      <p className="card-category">Total Revenue</p>
                      <Card.Title as="h5">
                        {totalRevenue.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 2,
                        })}
                      </Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={4}>
                  <Card className="card-stats revenue-disease">
                    <Card.Body>
                      <p className="card-category">Total Paid Consultations</p>
                      <Card.Title as="h5">{totalConsultations.toLocaleString()}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Table header */}
              <div className="datatable-title card d-flex flex-row justify-content-between align-items-center mb-0">
                <h4 className="card-title">Revenue by Disease</h4>
                {/* <div className="d-flex datatable-title filter-btns-holder">
                  <button onClick={refreshTable} type="button" className="outline-button w-auto">
                    <RiRefreshFill style={{ cursor: "pointer" }} color="#fff" size={28} />
                  </button>
                </div> */}
              </div>

              <DataTable
                columns={columns}
                data={filteredRows}
                pagination
                paginationPerPage={RECORDS_PER_PAGE}
                className="react-datatable"
                progressPending={loader}
                progressComponent={<FullPageLoader />}
                paginationComponent={() => (
                  <div className="pagination">
                    <div className="footer-pagination">
                      <div className="total_pages">
                        {rows.length === 0
                          ? "0 of 0"
                          : `${skip + 1}-${skip + rows.length} of ${totalRows.toLocaleString()}`}
                      </div>
                    </div>
                  </div>
                )}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default RevenueByDisease;
